import { ExitIntentAreas, TriggersConfigMap, TriggerType } from 'sections/onsite-campaigns/types';
import { WorkflowLabel } from '../../../WorkflowBox/WorkflowLabel';
import { useTranslation } from 'react-i18next';
import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { SvgIcon, theme } from '@salecycle/ui-component-library';
import styled from 'styled-components';
import { CampaignEditorState } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorState';
import { hasValidTriggers } from '../utils/hasValidTriggers';
import { hasActiveCampaignTriggers } from '../utils/hasActiveCampaignTriggers';

export type TriggerLevel = 'message' | 'campaign';

export type TriggersWorkflowLabelProps = {
  id: string;
  level: TriggerLevel;
};

export type ActiveTrigger = {
  level: TriggerLevel;
  triggers?: TriggersConfigMap;
};

const getActiveTriggers = (state: CampaignEditorState, messageId: string): ActiveTrigger => {
  const message = state?.edit?.messages[messageId] || state?.messages[messageId];

  if (message?.triggers && hasValidTriggers(message.triggers)) {
    return {
      level: 'message',
      triggers: message.triggers,
    };
  }

  if (hasActiveCampaignTriggers(state?.edit?.trigger || state?.trigger)) {
    return {
      level: 'campaign',
    };
  }
  return null;
};

const getTriggerDisplayText = (triggers: TriggersConfigMap) => {
  const displayText = [];

  if (triggers?.[TriggerType.ExitIntent]?.active && triggers?.[TriggerType.ExitIntent]?.value) {
    displayText.push(`${displayText} Exit Intent`);
  }

  if (triggers?.[TriggerType.Inactivity]?.active && triggers?.[TriggerType.Inactivity]?.value) {
    displayText.push('Inactivity');
    displayText.push(`${triggers?.[TriggerType.Inactivity]?.value}s`);
  }

  if (triggers?.[TriggerType.PageLoad]?.active && triggers?.[TriggerType.PageLoad]?.value === true) {
    displayText.push('Page Load');
  }

  if (triggers?.[TriggerType.OnClick]?.active && triggers?.[TriggerType.OnClick]?.value?.selector) {
    displayText.push('On Click');
  }

  return displayText.join(', ');
};

const TriggerContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.designToken.spacing1};
`;

export const TriggersWorkflowLabel = ({ id, level }: TriggersWorkflowLabelProps) => {
  const { t } = useTranslation('campaigns');
  const { state } = useCampaignEditorContext();

  const activeTriggers = getActiveTriggers(state, id);

  if (!activeTriggers) {
    return (
      <TriggerContainer>
        <WorkflowLabel
          label={`${t('onsite.editor.workflow.triggerMessage.workflow.eventsSelected')}:`}
          text={t('onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.noTriggersValidation')}
        />
        {level === 'message' && (
          <SvgIcon name="triangle-exclamation" color={theme.designToken.warning700} size={'24'} />
        )}
      </TriggerContainer>
    );
  }

  if (activeTriggers.level === 'campaign') {
    return (
      <WorkflowLabel
        label={`${t('onsite.editor.workflow.triggerMessage.workflow.eventsSelected')}:`}
        text={t('onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.campaignTriggersMessage')}
      />
    );
  }

  if (!activeTriggers?.triggers) {
    return null;
  }

  return (
    <>
      <WorkflowLabel
        label={`${t('onsite.editor.workflow.triggerMessage.workflow.eventsSelected')}:`}
        text={getTriggerDisplayText(activeTriggers?.triggers)}
      />
      {activeTriggers?.triggers?.[TriggerType.ExitIntent]?.active && (
        <WorkflowLabel
          label={`${t('onsite.editor.workflow.triggerMessage.workflow.edgesSelected')}:`}
          text={
            activeTriggers?.triggers[TriggerType.ExitIntent]?.value.length === Object.values(ExitIntentAreas)?.length
              ? t('onsite.editor.workflow.triggerMessage.workflow.allEdges')
              : activeTriggers?.triggers[TriggerType.ExitIntent]?.value.join(', ')
          }
        />
      )}
    </>
  );
};
